export const PROPERTY_OFFER_STATUS = Object.freeze({
  SUBMITTED: 'submitted',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  WITHDRAWN: 'withdrawn',
  PARTIALLY_SIGNED: 'partially_signed',
  SIGNED: 'signed',
});

export const PROPERTY_OFFER_KIND = Object.freeze({
  OFFER: 'offer',
  BOOKING: 'booking',
});

export const PROPERTY_OFFER_BUSINESS_CONTEXT = Object.freeze({
  PURCHASE: 'purchase',
  RENTAL: 'rental',
});

export const PROPERTY_OFFER_PAYMENT_KIND = Object.freeze({
  CASH: 'a_vista',
  FINANCING: 'financiamento',
  CASH_FGTS: 'a_vista_fgts',
  FINANCING_FGTS: 'financiamento_fgts',
});

export const PROPERTY_OFFER_BUCKET = Object.freeze({
  ONGOING: 'ongoing',
  CANCELED: 'canceled',
  FINISHED: 'finished',
});

export const OFFER_TABS = Object.freeze([
  Object.freeze({
    value: PROPERTY_OFFER_BUCKET.ONGOING,
    labelKey: 'PROPERTIES.OFFERS.TABS.ONGOING',
  }),
  Object.freeze({
    value: PROPERTY_OFFER_BUCKET.CANCELED,
    labelKey: 'PROPERTIES.OFFERS.TABS.CANCELED',
  }),
  Object.freeze({
    value: PROPERTY_OFFER_BUCKET.FINISHED,
    labelKey: 'PROPERTIES.OFFERS.TABS.FINISHED',
  }),
]);

export const OFFER_SORT_VALUES = Object.freeze({
  RECENT_ACTIVITY: 'updated_at,desc',
  ARRIVAL_DATE: 'created_at,desc',
  HIGHER_VALUE: 'o.offer_price,desc',
  LOWER_VALUE: 'o.offer_price,asc',
});

export const OFFER_SORT_OPTIONS = Object.freeze([
  Object.freeze({
    value: OFFER_SORT_VALUES.RECENT_ACTIVITY,
    labelKey: 'PROPERTIES.OFFERS.SORT.RECENT_ACTIVITY',
  }),
  Object.freeze({
    value: OFFER_SORT_VALUES.ARRIVAL_DATE,
    labelKey: 'PROPERTIES.OFFERS.SORT.ARRIVAL_DATE',
  }),
  Object.freeze({
    value: OFFER_SORT_VALUES.HIGHER_VALUE,
    labelKey: 'PROPERTIES.OFFERS.SORT.HIGHER_VALUE',
  }),
  Object.freeze({
    value: OFFER_SORT_VALUES.LOWER_VALUE,
    labelKey: 'PROPERTIES.OFFERS.SORT.LOWER_VALUE',
  }),
]);

export const OFFER_TYPE_FILTERS = Object.freeze([
  Object.freeze({
    referenceValue: 'CASH',
    value: PROPERTY_OFFER_PAYMENT_KIND.CASH,
    labelKey: 'PROPERTIES.OFFERS.PAYMENT_KIND.CASH',
  }),
  Object.freeze({
    referenceValue: 'FINANCED',
    value: PROPERTY_OFFER_PAYMENT_KIND.FINANCING,
    labelKey: 'PROPERTIES.OFFERS.PAYMENT_KIND.FINANCED',
  }),
  Object.freeze({
    referenceValue: 'CASH_USING_FGTS',
    value: PROPERTY_OFFER_PAYMENT_KIND.CASH_FGTS,
    labelKey: 'PROPERTIES.OFFERS.PAYMENT_KIND.CASH_FGTS',
  }),
  Object.freeze({
    referenceValue: 'FINANCED_USING_FGTS',
    value: PROPERTY_OFFER_PAYMENT_KIND.FINANCING_FGTS,
    labelKey: 'PROPERTIES.OFFERS.PAYMENT_KIND.FINANCED_FGTS',
  }),
]);

export const OFFER_MACRO_STATUS = Object.freeze({
  NEW: 'NEW',
  NEGOTIATION: 'NEGOTIATION',
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  CCV: 'CCV',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
});

export const OFFER_STEPS_BY_STATUS = Object.freeze({
  [OFFER_MACRO_STATUS.NEW]: Object.freeze([
    'nova',
    'submitted',
    'NEGOTIATION_NEW',
    'PROPOSAL_ON_VALIDATION',
  ]),
  [OFFER_MACRO_STATUS.NEGOTIATION]: Object.freeze([
    'negociacao',
    'NEGOTIATION_ONGOING',
    'PROPOSAL_ONGOING',
  ]),
  [OFFER_MACRO_STATUS.OFFER_ACCEPTED]: Object.freeze([
    'aceita',
    'accepted',
    'NEGOTIATION_OFFER_ACCEPTED',
    'PROPOSAL_ACCEPTED',
  ]),
  [OFFER_MACRO_STATUS.CCV]: Object.freeze([
    'analise_credito',
    'ccv_enviado',
    'ccv_assinado',
    'CREDIT_ANALYSIS',
    'CCV_SENT_TO_BE_SIGNED',
    'CCV_SIGNED',
  ]),
  [OFFER_MACRO_STATUS.CANCELED]: Object.freeze([
    'declined',
    'withdrawn',
    'CANCELED_CCV',
    'CANCELED_ON_VALIDATION',
    'CANCELED_ON_NEGOTIATION',
    'CANCELED_POST_ACCEPTED',
  ]),
  [OFFER_MACRO_STATUS.FINISHED]: Object.freeze([
    'finalizada',
    'signed',
    'OFFER_FINISHED',
    'PURCHASE_SALE_COMPLETED',
    'POST_CCV_HUB_CT',
    'POST_CCV',
  ]),
});

export const OFFER_STEPS_WITH_ALERT = Object.freeze([
  ...OFFER_STEPS_BY_STATUS[OFFER_MACRO_STATUS.CANCELED],
  ...OFFER_STEPS_BY_STATUS[OFFER_MACRO_STATUS.FINISHED],
]);

export const OFFER_STATUS_LABELS = Object.freeze({
  [PROPERTY_OFFER_STATUS.SUBMITTED]: 'Proposta recebida',
  [PROPERTY_OFFER_STATUS.ACCEPTED]: 'Proposta aceita',
  [PROPERTY_OFFER_STATUS.DECLINED]: 'Proposta cancelada',
  [PROPERTY_OFFER_STATUS.WITHDRAWN]: 'Proposta cancelada',
  [PROPERTY_OFFER_STATUS.PARTIALLY_SIGNED]: 'Assinatura parcial',
  [PROPERTY_OFFER_STATUS.SIGNED]: 'Contrato assinado',
});

export const OFFER_STATUS_META = Object.freeze({
  [PROPERTY_OFFER_STATUS.SUBMITTED]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.SUBMITTED],
    labelKey: 'PROPERTIES.OFFERS.STATUS.SUBMITTED',
    variant: 'attention',
    className: 'bg-sky-50 text-sky-700',
  }),
  [PROPERTY_OFFER_STATUS.ACCEPTED]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.ACCEPTED],
    labelKey: 'PROPERTIES.OFFERS.STATUS.ACCEPTED',
    variant: 'success',
    className: 'bg-emerald-50 text-emerald-700',
  }),
  [PROPERTY_OFFER_STATUS.DECLINED]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.DECLINED],
    labelKey: 'PROPERTIES.OFFERS.STATUS.DECLINED',
    variant: 'error',
    className: 'bg-rose-50 text-rose-700',
  }),
  [PROPERTY_OFFER_STATUS.WITHDRAWN]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.WITHDRAWN],
    labelKey: 'PROPERTIES.OFFERS.STATUS.WITHDRAWN',
    variant: 'neutral',
    className: 'bg-slate-100 text-slate-600',
  }),
  [PROPERTY_OFFER_STATUS.PARTIALLY_SIGNED]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.PARTIALLY_SIGNED],
    labelKey: 'PROPERTIES.OFFERS.STATUS.PARTIALLY_SIGNED',
    variant: 'info',
    className: 'bg-sky-50 text-sky-700',
  }),
  [PROPERTY_OFFER_STATUS.SIGNED]: Object.freeze({
    label: OFFER_STATUS_LABELS[PROPERTY_OFFER_STATUS.SIGNED],
    labelKey: 'PROPERTIES.OFFERS.STATUS.SIGNED',
    variant: 'success',
    className: 'bg-emerald-50 text-emerald-700',
  }),
});

export const OFFER_KIND_LABELS = Object.freeze({
  [PROPERTY_OFFER_KIND.BOOKING]: 'Reserva',
  [PROPERTY_OFFER_KIND.OFFER]: 'Proposta',
});

export const OFFER_BUSINESS_CONTEXT_LABELS = Object.freeze({
  [PROPERTY_OFFER_BUSINESS_CONTEXT.RENTAL]: 'Aluguel',
  [PROPERTY_OFFER_BUSINESS_CONTEXT.PURCHASE]: 'Venda',
});

export const OFFER_PAYMENT_KIND_LABELS = Object.freeze({
  [PROPERTY_OFFER_PAYMENT_KIND.CASH]: 'À vista',
  [PROPERTY_OFFER_PAYMENT_KIND.FINANCING]: 'Financiado',
  [PROPERTY_OFFER_PAYMENT_KIND.CASH_FGTS]: 'À vista + FGTS',
  [PROPERTY_OFFER_PAYMENT_KIND.FINANCING_FGTS]: 'Financiado + FGTS',
});

export default PROPERTY_OFFER_STATUS;
